import { useDownloadBill, useViewBill } from "@features/bx/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { useCurrency } from "@shared/selectors/globalSelectors";
import { TableAction, TableColumn } from "@shared/types/table";
import { toSnakeCase } from "@shared/utils/caseConverter";
import { PAGE_SIZE } from "@shared/utils/constants";
import { useState } from "react";
import { useSingleBillList } from "../hooks";
import { SingleBillFilters } from "./singlBillFilters";

interface SingleBill {
  id: string;
  invoiceNumber: string;
  consumer: string;
  premise: string;
  billCycle: string;
  billingMonth: string;
  consumption: number;
  billedAmount: number;
  generatedDate: string;
  status: "delivered" | "paid" | "overdue" | "draft";
}

interface SingleBillFiltersType {
  status: string;
  bill_cycle: string;
  billing_month: string;
  created_on_start: string;
  created_on_end: string;
  page: number;
}

const getStatusBadgeConfig = (status: SingleBill["status"]) => {
  switch (status) {
    case "paid":
      return {
        variant: "default" as const,
        className: "bg-green-100 text-green-800",
      };
    case "delivered":
      return {
        variant: "secondary" as const,
        className: "bg-blue-100 text-blue-800",
      };
    case "overdue":
      return {
        variant: "destructive" as const,
        className: "bg-red-100 text-red-800",
      };
    case "draft":
      return {
        variant: "outline" as const,
        className: "bg-yellow-100 text-yellow-800",
      };
    default:
      return {
        variant: "outline" as const,
        className: "bg-gray-100 text-gray-800",
      };
  }
};

const currentYear = new Date().getFullYear();

const months = [
  { label: "Jan", value: `1 ${currentYear}` },
  { label: "Feb", value: `2 ${currentYear}` },
  { label: "Mar", value: `3 ${currentYear}` },
  { label: "Apr", value: `4 ${currentYear}` },
  { label: "May", value: `5 ${currentYear}` },
  { label: "Jun", value: `6 ${currentYear}` },
  { label: "Jul", value: `7 ${currentYear}` },
  { label: "Aug", value: `8 ${currentYear}` },
  { label: "Sep", value: `9 ${currentYear}` },
  { label: "Oct", value: `10 ${currentYear}` },
  { label: "Nov", value: `11 ${currentYear}` },
  { label: "Dec", value: `12 ${currentYear}` },
];

export const SingleBillTable = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [filters, setFilters] = useState<SingleBillFiltersType>({
    status: "all",
    bill_cycle: "all",
    billing_month: "all",
    created_on_start: null,
    created_on_end: null,
    page: 1,
  });

  // Fixed: Use proper sortConfig state that matches GenericTable expectations
  const [sortConfig, setSortConfig] = useState<{
    field: string;
    direction: "asc" | "desc";
  } | null>(null);

  // Format filters for the hook
  const formatFiltersForHook = (
    filters: SingleBillFiltersType
  ): Record<string, any> => {
    const hookFilters: Record<string, any> = {
      page: currentPage,
      limit: PAGE_SIZE,
    };

    if (searchTerm.trim()) {
      hookFilters.search_data = searchTerm;
    }

    if (filters.status && filters.status !== "all") {
      hookFilters.status = filters.status;
    }

    if (filters.bill_cycle && filters.bill_cycle !== "all") {
      hookFilters.bill_cycle = filters.bill_cycle;
    }

    if (filters.billing_month && filters.billing_month !== "all") {
      hookFilters.billing_month = filters.billing_month;
    }

    if (filters.created_on_start) {
      hookFilters.created_on_start = String(filters.created_on_start);
    }
    if (filters.created_on_end) {
      hookFilters.date_to = String(filters.created_on_end);
    }

    // Fixed: Proper sorting parameter handling with +/- prefix format
    if (sortConfig) {
      const fieldName = toSnakeCase(sortConfig.field);
      hookFilters.sort =
        sortConfig.direction === "asc" ? `+${fieldName}` : `-${fieldName}`;
    }

    return hookFilters;
  };

  const downloadBill = useDownloadBill();
  const viewBill = useViewBill();

  const { data: billData, isLoading } = useSingleBillList(
    formatFiltersForHook(filters)
  );

  const handleSearch = (searchValue: string, paramName: string) => {
    setSearchTerm(searchValue);
    setCurrentPage(1);
  };

  const handleFiltersChange = (newFilters: SingleBillFiltersType) => {
    setFilters(newFilters);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setFilters({
      status: "all",
      bill_cycle: "all",
      billing_month: "all",
      created_on_start: null,
      created_on_end: null,
      page: 1,
    });
    setSearchTerm("");
    setCurrentPage(1);
    setSortConfig(null); // Fixed: Also clear sort when clearing filters
  };

  const handlePageChange = (newPage: number) => {
    setCurrentPage(newPage);
  };

  const handleDownload = async (billId: string, filename?: string) => {
    try {
      await downloadBill.mutateAsync({
        billId,
        filename: filename || `bill-${billId}.pdf`,
      });
      // showToast('Bill downloaded successfully', 'success');
    } catch (error) {
      console.error("Download failed:", error);
      // showToast('Failed to download the bill', 'error');
    }
  };

  const handleView = async (billId: string) => {
    try {
      await viewBill.mutateAsync(billId);
    } catch (error) {
      console.error("View failed:", error);
      // showToast('Failed to view the bill', 'error');
    }
  };

  const CURRENCY = useCurrency();

  // Fixed: Proper sort handler that updates sortConfig
  const handleSort = (field: string, direction: "asc" | "desc") => {
    setSortConfig({ field, direction });
    setCurrentPage(1);
  };

  // Define columns for the generic table
  const columns: TableColumn[] = [
    {
      key: "billNumber",
      header: "Invoice Number",
      type: "text",
    },
    {
      key: "remote_consumer_number",
      header: "Consumer",
      type: "text",
      sortable: true,
      render: (value, row) => (
        <div>
          <p className="font-medium">{row.consumerName}</p>
          <p className="text-xs text-muted-foreground">
            {row.remoteConsumerNumber}
          </p>
        </div>
      ),
    },
    {
      key: "premise",
      header: "Premise",
      type: "text",
    },
    {
      key: "billMonth",
      header: "Billing Month",
      type: "text",
    },
    {
      key: "consumptions",
      header: "Consumption (Units)",
      type: "custom",
      render: (value: number) => value?.toLocaleString() || "0",
    },
    {
      key: "billAmount",
      header: "Billed Amount",
      type: "custom",
      sortable: true,
      render: (value: number) => `${CURRENCY} ${value?.toFixed(2) || "0.00"}`,
    },
    {
      key: "createdDate",
      header: "Generated Date",
      type: "text",
      sortable: true,
    },
    {
      key: "statusDisplay",
      header: "Status",
      type: "badge",
      badgeConfig: {
        getVariant: (value: string) =>
          getStatusBadgeConfig(value as SingleBill["status"]).variant,
        getClassName: (value: string) =>
          getStatusBadgeConfig(value as SingleBill["status"]).className,
      },
    },
  ];

  // Define actions for the generic table
  const actions: TableAction<SingleBill>[] = [
    {
      label: "View",
      onClick: (bill) => {
        handleView(bill.id);
      },
      icon: "Eye",
      variant: "outline",
    },
    {
      label: "Download",
      onClick: (bill) => {
        handleDownload(bill.id);
      },
      icon: "Download",
      variant: "outline",
    },
  ];

  // Calculate pagination info
  const totalItems = billData?.count || 0;
  const totalPages = Math.ceil(totalItems / PAGE_SIZE);
  const startItem = (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

  return (
    <div className="space-y-4">
      <GenericTable
        data={billData?.result ?? []}
        columns={columns}
        rowKey="id"
        loading={isLoading}
        searchPlaceholder="Search by invoice, consumer, or premise..."
        searchParamName="query"
        searchDebounceMs={300}
        onSearch={handleSearch}
        actions={actions}
        actionsType="icons"
        emptyMessage="No bills found"
        sortConfig={sortConfig}
        onSort={handleSort}
        filterPanel={
          <SingleBillFilters
            filters={filters}
            onApplyFilters={handleFiltersChange}
            onFiltersChange={handleFiltersChange}
            months={months}
            onClearFilters={clearFilters}
          />
        }
        pagination={{
          currentPage: currentPage,
          totalPages: totalPages,
          totalItems,
          itemsPerPage: PAGE_SIZE,
          startItem: startItem,
          endItem: endItem,
        }}
        onPageChange={handlePageChange}
      />
    </div>
  );
};
